import React, { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Pagination } from "swiper/modules";
import { ImQuotesRight } from "react-icons/im";
import { TestimonialsConstants } from "./TestimonialsConstants";
import fire from "../../../assets/images/icons/fire.svg";
import { Images } from "../../../utils/imageConstants";
import "./Testimonials.scss";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const Testimonials: React.FC = () => {
    const { testimonials } = TestimonialsConstants;
    const testimonialItems = testimonials.items;
    const hasMultipleTestimonials = testimonialItems.length > 1;
    const swiperRef = useRef<SwiperType | null>(null);
    const [isBeginning, setIsBeginning] = useState(!hasMultipleTestimonials);
    const [isEnd, setIsEnd] = useState(!hasMultipleTestimonials);

    const handleSlideChange = (swiper: SwiperType) => {
        if (!hasMultipleTestimonials) return;
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
    };

    return (
        <section className="testimonials-section">
            <div className="testimonials-container">
                <div className="testimonials-header">
                    <div className="testimonials-header-left">
                        <div className="testimonials-tag">
                            <img src={fire} alt="Flame icon" className="tag-icon" />
                            <span className="tag-text">{testimonials.tag}</span>
                        </div>
                        <h2 className="testimonials-title">{testimonials.title}</h2>
                    </div>

                    <div className="testimonials-header-right">
                        <p className="testimonials-description">
                            {testimonials.description}
                        </p>
                    </div>
                </div>

                <div className="testimonials-carousel-wrapper">
                    <Swiper
                        modules={[Pagination]}
                        centeredSlides={hasMultipleTestimonials}
                        watchSlidesProgress={hasMultipleTestimonials}
                        spaceBetween={26}
                        slidesPerView={hasMultipleTestimonials ? 2.35 : 1}
                        slidesPerGroup={1}
                        slideToClickedSlide={hasMultipleTestimonials}
                        speed={450}
                        allowTouchMove={hasMultipleTestimonials}
                        pagination={
                            hasMultipleTestimonials
                                ? {
                                    clickable: true,
                                    el: ".testimonials-pagination",
                                    bulletClass: "testimonial-bullet",
                                    bulletActiveClass: "testimonial-bullet-active",
                                }
                                : false
                        }
                        onSwiper={(swiper: SwiperType) => {
                            swiperRef.current = swiper;
                            if (hasMultipleTestimonials) {
                                setIsBeginning(swiper.isBeginning);
                                setIsEnd(swiper.isEnd);
                            }
                        }}
                        onSlideChange={handleSlideChange}
                        breakpoints={{
                            0: {
                                slidesPerView: 1,
                                spaceBetween: 16,
                                centeredSlides: hasMultipleTestimonials,
                                slidesPerGroup: 1
                            },
                            640: {
                                slidesPerView: hasMultipleTestimonials ? 1.6 : 1,
                                spaceBetween: 20,
                                centeredSlides: hasMultipleTestimonials,
                                slidesPerGroup: 1
                            },
                            768: {
                                slidesPerView: hasMultipleTestimonials ? 2.35 : 1,
                                spaceBetween: 26,
                                centeredSlides: hasMultipleTestimonials,
                                slidesPerGroup: 1
                            },
                        }}
                        className={`testimonials-swiper ${!hasMultipleTestimonials ? "testimonials-swiper--single" : ""}`}
                    >
                        {testimonialItems.map((testimonial) => (
                            <SwiperSlide key={testimonial.id}>
                                <div className="testimonial-card">
                                    <div className="testimonial-speech-bubble"></div>
                                    <p className="testimonial-text">
                                        {testimonial.text}
                                    </p>
                                    <ImQuotesRight className="testimonial-quote-icon" aria-hidden="true" />
                                </div>

                                <div className="testimonial-author">
                                    <img
                                        src={Images.dummy_profile}
                                        alt={testimonial.author}
                                        className="author-avatar"
                                    />
                                    <div className="author-meta">
                                        <div className="author-name">
                                            {testimonial.author}
                                        </div>
                                        <div className="author-title">
                                            {testimonial.title} at {testimonial.company}
                                        </div>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    {hasMultipleTestimonials && (
                    <div className="testimonials-controls">
                        <button
                            className="testimonials-nav-btn testimonials-nav-prev"
                            disabled={isBeginning}
                            aria-label="Previous testimonial"
                            type="button"
                            onClick={() => swiperRef.current?.slidePrev()}
                        >
                            ‹
                        </button>

                        <div className="testimonials-pagination"></div>

                        <button
                            className="testimonials-nav-btn testimonials-nav-next"
                            disabled={isEnd}
                            aria-label="Next testimonial"
                            type="button"
                            onClick={() => swiperRef.current?.slideNext()}
                        >
                            ›
                        </button>
                    </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default Testimonials;
